'use client'

import { useState, useRef, useCallback, useEffect } from 'react'
// Using regular img tag instead of Next.js Image component

import { X, Upload, Eye, Trash2, Edit3, Download, RotateCw } from 'lucide-react'

interface PropertyImage {
  id: string
  url: string
  // Cloudinary publicId (optional, available when uploaded through backend)
  publicId?: string
  name: string
  size: number
  type: string
  uploaded_at: string
  is_primary: boolean
  order: number
}

interface PropertyImageManagerProps {
  propertyId: string
  initialImages?: string[]
  onImagesChange: (images: string[]) => void
  maxImages?: number
  maxFileSize?: number // in MB
}

export default function PropertyImageManager({
  propertyId: _propertyId,
  initialImages = [],
  onImagesChange,
  maxImages = 20,
  maxFileSize = 10
}: PropertyImageManagerProps) {
  const [images, setImages] = useState<PropertyImage[]>([])
  const [uploading, setUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [selectedImage, setSelectedImage] = useState<PropertyImage | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [reordering, setReordering] = useState(false)
  const [message, setMessage] = useState('')
  
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dropRef = useRef<HTMLDivElement>(null)

  // Get API URL once at component level
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
  const apiBaseUrl = apiUrl.replace(/\/api\/?$/, '') // Remove /api to get base URL

  // Helper to normalize image URLs to use the correct API base URL
  const normalizeImageUrl = (url: string): string => {
    if (!url) return url
    
    // If the URL is absolute and points to localhost:5001, convert it to use the API base URL
    if (url.includes('localhost:5001') || url.includes('127.0.0.1:5001')) {
      try {
        const urlObj = new URL(url)
        return `${apiBaseUrl}${urlObj.pathname}${urlObj.search}${urlObj.hash}`
      } catch {
        // If URL parsing fails, try simple string replacement
        return url.replace(/https?:\/\/[^/]+/, apiBaseUrl)
      }
    } else if (url.startsWith('/uploads')) {
      // If it's already a relative path, prepend the API base URL
      return `${apiBaseUrl}${url}`
    } else if (url.startsWith('uploads')) {
      // If it's a relative path without leading slash
      return `${apiBaseUrl}/${url}`
    }
    
    // Return as-is if it's already a valid URL or relative path
    return url
  }

  // Initialize images from props
  useEffect(() => {
    if (initialImages.length > 0) {
      const formattedImages: PropertyImage[] = initialImages.map((url, index) => ({
        id: `existing-${index}`,
        url: normalizeImageUrl(url),
        name: `image-${index + 1}.jpg`,
        size: 0,
        type: 'image/jpeg',
        uploaded_at: new Date().toISOString(),
        is_primary: index === 0,
        order: index
      }))
      setImages(formattedImages)
    }
  }, [initialImages])

  // Handle drag and drop
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files))
    }
  }, [])

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(Array.from(e.target.files))
    }
  }

  // Process uploaded files
  const handleFiles = async (files: File[]) => {
    if (images.length + files.length > maxImages) {
      setMessage(`Maximum ${maxImages} images allowed. Please remove some images first.`)
      return
    }

    setUploading(true)
    setMessage('')

    try {
      const newImages: PropertyImage[] = []

      for (const file of files) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          setMessage(`${file.name} is not an image file.`)
          continue
        }

        // Validate file size
        if (file.size > maxFileSize * 1024 * 1024) {
          setMessage(`${file.name} is too large. Maximum size is ${maxFileSize}MB.`)
          continue
        }

        // Upload image to backend
        const token = localStorage.getItem('authToken')
        if (!token) {
          setMessage('Please log in to upload images')
          continue
        }

        const formData = new FormData()
        formData.append('file', file)

        const response = await fetch(`${apiUrl}/upload/single`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            // Don't set Content-Type header - let browser set it with boundary for FormData
          },
          body: formData
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          setMessage(`Failed to upload ${file.name}: ${errorData.message || 'Unknown error'}`)
          continue
        }

        const data = await response.json()
        // The response structure is { file: { url: ... } } or { url: ... }
        let imageUrl = data.file?.url || data.url
        const publicId = data.file?.publicId || data.publicId
        
        if (!imageUrl) {
          setMessage(`Failed to upload ${file.name}: No URL returned from server`)
          continue
        }

        // Normalize the URL - if it's an absolute URL with localhost:5001, convert to use VITE_API_URL base
        // This fixes CORS issues when loading images
        if (imageUrl.includes('localhost:5001') || imageUrl.includes('127.0.0.1:5001')) {
          try {
            const urlObj = new URL(imageUrl);
            imageUrl = `${apiBaseUrl}${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
          } catch {
            imageUrl = imageUrl.replace(/https?:\/\/[^/]+/, apiBaseUrl);
          }
        } else if (imageUrl.startsWith('/uploads')) {
          // If it's already a relative path, prepend the API base URL
          imageUrl = `${apiBaseUrl}${imageUrl}`;
        } else if (imageUrl.startsWith('uploads')) {
          // If it's a relative path without leading slash
          imageUrl = `${apiBaseUrl}/${imageUrl}`;
        }

        const urlData = { publicUrl: imageUrl }

        // Create image object
        const newImage: PropertyImage = {
          // Prefer Cloudinary publicId for stable identification (helps deletion)
          id: publicId || `new-${Date.now()}-${Math.random()}`,
          url: urlData.publicUrl,
          publicId: publicId,
          name: file.name,
          size: file.size,
          type: file.type,
          uploaded_at: new Date().toISOString(),
          is_primary: images.length === 0 && newImages.length === 0,
          order: images.length + newImages.length
        }

        newImages.push(newImage)
      }

      // Update images state
      const updatedImages = [...images, ...newImages]
      setImages(updatedImages)
      
      // Notify parent component
      onImagesChange(updatedImages.map(img => img.url))
      
      setMessage(`${newImages.length} image(s) uploaded successfully!`)
    } catch (error) {
      console.error('Upload error:', error)
      setMessage(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setUploading(false)
    }
  }

  // Set primary image
  const setPrimaryImage = (imageId: string) => {
    const updatedImages = images.map(img => ({
      ...img,
      is_primary: img.id === imageId
    }))
    setImages(updatedImages)
    onImagesChange(updatedImages.map(img => img.url))
  }

  // Remove image
  const removeImage = async (imageId: string) => {
    const imageToRemove = images.find(img => img.id === imageId)
    if (!imageToRemove) return

    // Don't allow removing primary image if it's the only one
    if (imageToRemove.is_primary && images.length === 1) {
      setMessage('Cannot remove the only image. Please upload a new image first.')
      return
    }

    try {
      // If it's a newly uploaded image (or we have a Cloudinary publicId), remove from storage too.
      // This prevents orphaned files in Cloudinary when users remove images before saving the property.
      if (imageToRemove.publicId || !imageToRemove.id.startsWith('existing-')) {
        const token = localStorage.getItem('authToken')
        if (token) {
          await fetch(`${apiUrl}/upload`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              publicId: imageToRemove.publicId,
              url: imageToRemove.publicId ? undefined : imageToRemove.url
            })
          }).catch(() => {
            // Best-effort cleanup. Even if this fails, we still remove it from UI.
          })
        }
      }

      // Update images state
      const updatedImages = images.filter(img => img.id !== imageId)
      
      // If primary image was removed, set first image as primary
      if (imageToRemove.is_primary && updatedImages.length > 0) {
        updatedImages[0].is_primary = true
      }

      // Reorder remaining images
      const reorderedImages = updatedImages.map((img, index) => ({
        ...img,
        order: index
      }))

      setImages(reorderedImages)
      onImagesChange(reorderedImages.map(img => img.url))
      setMessage('Image removed successfully!')
    } catch (error) {
      console.error('Remove error:', error)
      setMessage('Failed to remove image. Please try again.')
    }
  }

  // Reorder images
  const moveImage = (fromIndex: number, toIndex: number) => {
    const updatedImages = [...images]
    const [movedImage] = updatedImages.splice(fromIndex, 1)
    updatedImages.splice(toIndex, 0, movedImage)
    
    // Update order
    const reorderedImages = updatedImages.map((img, index) => ({
      ...img,
      order: index
    }))
    
    setImages(reorderedImages)
    onImagesChange(reorderedImages.map(img => img.url))
  }

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Get image dimensions for preview (removed for now)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Property Images</h3>
          <p className="text-sm text-gray-600">
            Upload and manage images for your property. Primary image will be displayed first.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setReordering(!reordering)}
            className={`px-3 py-2 text-sm rounded-md border ${
              reordering 
                ? 'bg-blue-600 text-white border-blue-600' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Edit3 className="w-4 h-4 inline mr-1" />
            {reordering ? 'Done' : 'Reorder'}
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || images.length >= maxImages}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-4 h-4 inline mr-1" />
            Add Images
          </button>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`p-3 rounded-md ${
          message.includes('successfully') 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Upload Area */}
      {images.length < maxImages && (
        <div
          ref={dropRef}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive 
              ? 'border-blue-400 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-900 mb-2">
            {dragActive ? 'Drop images here' : 'Drag & drop images here'}
          </p>
          <p className="text-sm text-gray-600 mb-4">
            or{' '}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              browse files
            </button>
          </p>
          <p className="text-xs text-gray-500">
            Supported formats: JPG, PNG, GIF • Max size: {maxFileSize}MB • Max images: {maxImages}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* Images Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div
              key={image.id}
              className={`relative group border rounded-lg overflow-hidden ${
                image.is_primary ? 'ring-2 ring-blue-500' : 'border-gray-200'
              }`}
            >
              {/* Image */}
              <div className="aspect-square relative bg-gray-100">
                <img
                  src={image.url}
                  alt={image.name}
                  className="w-full h-full object-cover"
                />
                
                {/* Primary Badge */}
                {image.is_primary && (
                  <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                    Primary
                  </div>
                )}

                {/* Order Number (when reordering) */}
                {reordering && (
                  <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-full">
                    {index + 1}
                  </div>
                )}

                {/* Hover Actions */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                  <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button
                      onClick={() => {
                        setSelectedImage(image)
                        setShowPreview(true)
                      }}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      title="Preview"
                    >
                      <Eye className="w-4 h-4 text-gray-700" />
                    </button>
                    
                    {!image.is_primary && (
                      <button
                        onClick={() => setPrimaryImage(image.id)}
                        className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                        title="Set as primary"
                      >
                        <RotateCw className="w-4 h-4 text-gray-700" />
                      </button>
                    )}
                    
                    <button
                      onClick={() => removeImage(image.id)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>

                {/* Reorder Handles */}
                {reordering && (
                  <div className="absolute bottom-2 left-2 right-2 flex justify-between">
                    {index > 0 && (
                      <button
                        onClick={() => moveImage(index, index - 1)}
                        className="p-1 bg-white rounded-full hover:bg-gray-100 transition-colors"
                        title="Move up"
                      >
                        ↑
                      </button>
                    )}
                    {index < images.length - 1 && (
                      <button
                        onClick={() => moveImage(index, index + 1)}
                        className="p-1 bg-white rounded-full hover:bg-gray-100 transition-colors"
                        title="Move down"
                      >
                        ↓
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Image Info */}
              <div className="p-3">
                <p className="text-sm font-medium text-gray-900 truncate" title={image.name}>
                  {image.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(image.size)} • {new Date(image.uploaded_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Uploading images...</p>
        </div>
      )}

      {/* Image Preview Modal */}
      {showPreview && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">{selectedImage.name}</h3>
              <div className="flex space-x-2">
                <a
                  href={selectedImage.url}
                  download={selectedImage.name}
                  className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </a>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
                  title="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="p-4">
              <div className="relative max-h-[70vh] overflow-hidden rounded-lg">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.name}
                  className="w-full h-auto object-contain"
                />
              </div>
              
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">File Size:</span>
                  <span className="ml-2 text-gray-900">{formatFileSize(selectedImage.size)}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Upload Date:</span>
                  <span className="ml-2 text-gray-900">
                    {new Date(selectedImage.uploaded_at).toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">File Type:</span>
                  <span className="ml-2 text-gray-900">{selectedImage.type}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Status:</span>
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                    selectedImage.is_primary 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedImage.is_primary ? 'Primary' : 'Secondary'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Stats */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex justify-between items-center text-sm">
          <span className="text-gray-600">
            {images.length} of {maxImages} images used
          </span>
          <span className="text-gray-600">
            Total size: {formatFileSize(images.reduce((sum, img) => sum + img.size, 0))}
          </span>
        </div>
        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(images.length / maxImages) * 100}%` }}
          />
        </div>
      </div>
    </div>
  )
}


